import React, { useState, useEffect } from 'react';
import { Challenge, UserProgress } from '../../types';
import { challengeAPI, userAPI, containerAPI } from '../../services/api';
import { ChallengeCard } from './ChallengeCard';
import { ChallengeView } from '../Challenge/ChallengeView';
import { Server, Shield, Network, Trophy, Target, Clock, Zap, CheckCircle } from 'lucide-react';
import { TerminalModal } from '../Challenge/TerminalModal';
import { useNavigate, useLocation } from 'react-router-dom';

export const Dashboard: React.FC = () => {
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [userProgress, setUserProgress] = useState<UserProgress[]>([]);
  const [selectedChallenge, setSelectedChallenge] = useState<Challenge | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'easy' | 'medium' | 'hard' | 'completed'>('all');
  const [consoleUrl, setConsoleUrl] = useState<string | null>(null);
  const [activeContainers, setActiveContainers] = useState<any[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('Fetching challenges and progress...');
        const [challengesData, progressData] = await Promise.all([
          challengeAPI.getChallenges(),
          userAPI.getProgress()
        ]);
        console.log('Challenges data:', challengesData);
        console.log('Progress data:', progressData);
        setChallenges(challengesData);
        setUserProgress(progressData);
        setLoadError(null);
      } catch (error: any) {
        console.error('Error fetching data:', error);
        // Only set error if we couldn't get any data
        if (challenges.length === 0 && userProgress.length === 0) {
          let errorMessage = 'Failed to load user data. Please try again.';
          
          if (error.response) {
            // Server responded with an error
            if (error.response.status === 401) {
              errorMessage = 'Your session has expired. Please log in again.';
            } else if (error.response.status === 403) {
              errorMessage = 'You do not have permission to access this page.';
            } else if (error.response.status === 404) {
              errorMessage = 'The requested resource was not found.';
            } else if (error.response.status >= 500) {
              errorMessage = 'Server error. Please try again later.';
            } else if (error.response.data?.detail) {
              errorMessage = error.response.data.detail;
            }
          } else if (error.request) {
            // Request was made but no response received
            if (error.message.includes('timeout')) {
              errorMessage = 'Request timed out. Please check your connection and try again.';
            } else {
              errorMessage = 'Network error. Please check your connection and try again.';
            }
          }
          
        setLoadError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    };

    const fetchActive = async () => {
      try {
        console.log('Fetching active containers...');
        const res = await containerAPI.getActiveContainers();
        console.log('Active containers:', res);
        setActiveContainers(res.containers || []);
      } catch (err: any) {
        console.error('Error fetching active containers:', err);
        setActiveContainers([]);
      }
    };

    fetchData();
    fetchActive();

    // If navigated from ChallengeDetail with startChallengeId, start the challenge
    if (location.state && location.state.startChallengeId) {
      onStartChallenge(location.state.startChallengeId);
      // Clear the state so it doesn't trigger again
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location]);

  const getUserProgressForChallenge = (challengeId: number) => {
    return userProgress.find(p => p.challenge_id === challengeId);
  };

  const getFilteredChallenges = () => {
    return challenges.filter(challenge => {
      if (filter === 'all') return true;
      if (filter === 'completed') {
        const progress = getUserProgressForChallenge(challenge.id);
        return progress?.completed || false;
      }
      return challenge.difficulty.toLowerCase() === filter;
    });
  };

  const stats = {
    total: challenges.length,
    completed: userProgress.filter(p => p.completed).length,
    totalPoints: userProgress.reduce((sum, p) => sum + (p.points_earned || 0), 0),
  };

  const onStartChallenge = async (challengeId: number) => {
    try {
      const data = await challengeAPI.startChallenge(challengeId);
      const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const wsHost = window.location.host;
      const wsUrl = `${wsProtocol}://${wsHost}/ws/${data.container_id}`;
      setConsoleUrl(wsUrl);
    } catch (err) {
      alert('Failed to start challenge. Please try again.');
    }
  };

  const handleResumeConsole = (sessionId: string) => {
    console.log('Resuming console with session ID:', sessionId);
    setConsoleUrl(sessionId);
  };

  if (selectedChallenge) {
    return (
      <ChallengeView 
        challenge={selectedChallenge} 
        onBack={() => setSelectedChallenge(null)} 
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[80vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading challenges...</p>
          {loadError && (
            <div className="text-red-500 mt-4">
              {loadError}
              <button className="underline ml-2" onClick={() => window.location.reload()}>Retry</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex items-center justify-center min-h-[80vh]">
        <div className="text-center">
          <div className="text-red-500 mb-4">{loadError}</div>
          <button 
            className="btn-primary" 
            onClick={() => {
              setLoading(true);
              setLoadError(null);
              window.location.reload();
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const filteredChallenges = getFilteredChallenges();

  return (
    <div className="py-12">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16 animate-fade-in">
          <h1 className="text-5xl font-bold mb-6">
            Master DevOps Through{' '}
            <span className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
              Real Challenges
            </span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto animate-slide-up">
            Practice troubleshooting real server issues in a safe, isolated environment. 
            Build confidence with hands-on experience that mirrors production scenarios.
          </p>
        </div>

        {/* Welcome Message for New Users */}
        {stats.completed === 0 && (
          <div className="card p-6 mb-12 animate-fade-in">
            <div className="flex items-start space-x-4">
              <div className="bg-blue-500/20 rounded-lg p-3">
                <Target className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Welcome to OpsQuest! 🚀</h3>
                <p className="text-gray-300 mb-4">
                  Ready to start your DevOps journey? Here's what you can do:
                </p>
                <ul className="space-y-3 text-gray-400">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span>Start with the "Fix SSH Configuration" challenge - perfect for beginners!</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span>Use real Linux commands in an isolated environment</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span>Track your progress and earn points</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="card p-6 text-center">
            <Trophy className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-white">{stats.completed}/{stats.total}</div>
            <div className="text-gray-400">Challenges Completed</div>
          </div>
          <div className="card p-6 text-center">
            <Target className="w-8 h-8 text-blue-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-white">{stats.totalPoints}</div>
            <div className="text-gray-400">Points Earned</div>
          </div>
          <div className="card p-6 text-center">
            <Zap className="w-8 h-8 text-purple-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-white">
              {stats.completed > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}%
            </div>
            <div className="text-gray-400">Progress</div>
          </div>
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="text-center p-6 card">
            <Server className="w-12 h-12 text-blue-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Real Environments</h3>
            <p className="text-gray-400">Work with actual Linux containers and real server configurations</p>
          </div>
          <div className="text-center p-6 card">
            <Shield className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Safe Practice</h3>
            <p className="text-gray-400">Isolated containers ensure you can experiment without consequences</p>
          </div>
          <div className="text-center p-6 card">
            <Network className="w-12 h-12 text-purple-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Progressive Learning</h3>
            <p className="text-gray-400">Challenges from basic troubleshooting to complex system debugging</p>
          </div>
        </div>

        {/* Filter buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          {[
            { key: 'all', label: 'All Challenges', icon: Target },
            { key: 'easy', label: 'Easy', icon: Target },
            { key: 'medium', label: 'Medium', icon: Clock },
            { key: 'hard', label: 'Hard', icon: Zap },
            { key: 'completed', label: 'Completed', icon: Trophy },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setFilter(key as any)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                filter === key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700/50 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* Challenges Grid */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            {filter === 'all' ? 'All Challenges' : 
             filter === 'completed' ? 'Completed Challenges' : 
             `${filter.charAt(0).toUpperCase() + filter.slice(1)} Challenges`}
          </h2>
          
          {filteredChallenges.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredChallenges.map((challenge) => {
                const activeContainer = activeContainers.find(c => c.challenge_id === challenge.id);
                return (
                  <ChallengeCard
                    key={challenge.id}
                    challenge={challenge}
                    onClick={() => navigate(`/challenges/${challenge.id}`)}
                    userProgress={getUserProgressForChallenge(challenge.id)}
                    activeContainer={activeContainer}
                    onResumeConsole={handleResumeConsole}
                  />
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg">
                {filter === 'completed' 
                  ? 'No challenges completed yet. Start with an easy challenge!' 
                  : `No ${filter} challenges available.`}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Resume Console Section */}
      {activeContainers.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-white mb-4">Resume Active Consoles</h2>
          <div className="flex flex-wrap gap-4">
            {activeContainers.map((c) => (
              <button
                key={c.container_id}
                className="btn-primary px-4 py-2 rounded"
                onClick={() => setConsoleUrl(c.session_id)}
              >
                {c.title || `Challenge #${c.challenge_id}`} (Started: {new Date(c.started_at).toLocaleString()})
              </button>
            ))}
          </div>
        </div>
      )}

      {consoleUrl && (
        <TerminalModal sessionId={consoleUrl} onClose={() => setConsoleUrl(null)} />
      )}
    </div>
  );
};
